/**
 * Centralized custom icons for consistent styling across the app.
 * These icons complement the Heroicons library with custom designs.
 */
import React from 'react';

interface IconProps {
  className?: string;
}

/**
 * Pencil/Edit icon - compact design for edit buttons
 */
export function PencilIcon({ className = "w-4 h-4" }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

/**
 * Trash/Delete icon - for delete actions
 */
export function TrashIcon({ className = "w-4 h-4" }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

/**
 * Close/X icon - accessible close icon for dismiss actions
 */
export function CloseIcon({ className = "w-5 h-5" }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

/**
 * AI Sparkle icon - three-spark design for AI/generation features
 * Consistent with "Get AI Recommendations" button styling
 */
export function AISparkleIcon({ className = "w-5 h-5" }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {/* Three-spark AI icon — large on right, two small sparks on the
          left. The lower-left spark is a touch bigger than the upper-left
          one and nudged down-left along the 45° diagonal so the small
          pair reads as a descending trail toward the large spark.

          The group is translated down by 0.5 viewBox units so the icon's
          visual center sits a hair below the geometric middle of the
          viewBox — that aligns better with adjacent text x-height when
          the icon flows inline (e.g. the Account page's "Automated
          Budget Optimization" banner). Individual path coordinates below
          are documented in the pre-transform local space. */}
      <g transform="translate(0, 0.5)">
        {/* Small spark (upper) — center (3.0, 7.9), arm 3.2, notch 0.8.
            The leftmost tip sits at x=-0.2, which clips imperceptibly even
            at small render sizes — the visual shift left is worth that. */}
        <path d="M3 11.1L3.8 8.7L6.2 7.9L3.8 7.1L3 4.7L2.2 7.1L-0.2 7.9L2.2 8.7L3 11.1Z" />
        {/* Small spark (lower) — center (4.8, 16.2), arm 4.0, notch 1.0 */}
        <path d="M4.8 20.2L5.8 17.2L8.8 16.2L5.8 15.2L4.8 12.2L3.8 15.2L0.8 16.2L3.8 17.2L4.8 20.2Z" />
        {/* Large spark — unchanged */}
        <path d="M14.5 21L16.2 15.2L22 13.5L16.2 11.8L14.5 6L12.8 11.8L7 13.5L12.8 15.2L14.5 21Z" />
      </g>
    </svg>
  );
}
